import React, { useContext } from "react";
import {
  fieldValue,
  firebase,
  firestore,
} from "services/firebaseService/firebaseConfig";
import { useSelector } from "react-redux";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { selectCurrentEventId } from "modules/event/selectors";
import { deleteAudienceViewReactions } from "modules/broadcast/AudienceView/actions";
import useLoginUser from "modules/auth/hooks/useLoginUser";
import logger from "logging/logger";
import { useAppDispatch } from "store/hooks";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { Actions, trackAction } from "modules/monitoring";
import { selectIsMobile } from "modules/deviceInfo";
import { EmojiReactionType, IEmojiReactionPayload } from "../../types";
import { scheduleAddReaction } from "../../redux/actions";

const QUERY_CUTOFF_TIME = 5_000;

export const ReactionEffects = () => {
  const isMobileView = useSelector(selectIsMobile);
  const { serverTime } = useSystemClock();
  const eventId = useSelector(selectCurrentEventId);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (!eventId || isMobileView) return;

    const unsubscribe = firestore
      .collection("EmojiReactions")
      .doc(eventId)
      .collection("reactions")
      .where(
        "time",
        ">",
        firebase.firestore.Timestamp.fromMillis(serverTime - QUERY_CUTOFF_TIME),
      )
      .onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            dispatch(
              scheduleAddReaction(change.doc.data() as IEmojiReactionPayload),
            );
          }
        });
      });

    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribe();
      dispatch(deleteAudienceViewReactions());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // serverTime is not needed in dependencies:
    // this query is registered once and callback runs on every new child
    eventId,
    isMobileView,
  ]);

  return null;
};

const useEmojiReactions = () => {
  const { userId } = useLoginUser();
  const eventId = useSelector(selectCurrentEventId);
  const { track } = useContext(TRACKING_CONTEXT);

  const addReaction = async (reactionType: EmojiReactionType) => {
    if (!userId || !eventId) return;

    track(Events.AUDIENCE_REACTION_CLICKED, {
      reaction: reactionType,
      eventId,
    });

    trackAction(Actions.PRESENTATION_MODE_AUDIENCE_REACTION_CLICKED, {
      reaction: reactionType,
      eventId,
      userId,
    });

    try {
      await firestore
        .collection("EmojiReactions")
        .doc(eventId)
        .collection("reactions")
        .add({
          time: fieldValue.serverTimestamp(),
          reactionType,
          id: btoa(Date.now() + userId),
          userId,
        });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      logger.error(
        `[useEmojiReactions][addEmojiReaction] err: ${errorMessage} event: ${eventId}`,
      );
    }
  };

  return {
    addReaction,
  };
};

export default useEmojiReactions;
